/**
 * Message Utility Functions
 * Helper functions for message processing, URL detection, and metadata building
 */

import { Attachment, LinkPreviewMetadata, LinkReviewBody } from "../types/sendMessage";

/**
 * Regex pattern for detecting mentions in server format
 * Format: @[DisplayName](userid:user-id)
 */
export const MENTION_REGEX = /@\[([^\]]+)\]\(userid:([a-zA-Z0-9-]+)\)/g;

/**
 * Format mentions in message for display as plain text
 * Converts @[Name](userid:id) to @Name
 * @param text - Text with mentions in server format
 * @returns Plain text with formatted mentions
 */
export const formatMentionsForDisplayText = (text: string): string => {
  // Reset regex lastIndex
  MENTION_REGEX.lastIndex = 0;
  return text.replace(MENTION_REGEX, "@$1");
};

/**
 * Find all URLs in text using regex
 * @param text - Text to search for URLs
 * @returns Array of URLs found in text
 */
export const findUrlsInText = (text: string): string[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = text.match(urlRegex);
  return matches || [];
};

/**
 * Generate a unique GUID for temporary IDs
 * @returns A unique GUID string
 */
export const generateGUID = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Build metadata object from attachments array
 * @param attachments - Array of attachment objects
 * @returns Metadata object for API
 */
export const buildAttachmentMetadata = (attachments: Attachment[]): Record<string, any> => {
  return {
    attachments: attachments.map((file) => ({
      url: file.url,
      name: file.name || "file",
      mimeType: file.mimeType || "application/octet-stream",
      size: file.size || 0,
      width: file.width,
      height: file.height,
      duration: file.duration,
      thumbnailUrl: file.thumbnailUrl,
    })),
  };
};

/**
 * Build link preview metadata from URL and link review data
 * @param url - The URL to preview
 * @param linkData - Link review data from backend
 * @returns Link preview metadata object
 */
export const buildLinkPreviewMetadata = (url: string, linkData: LinkReviewBody): LinkPreviewMetadata => {
  const urlObject = new URL(url);

  return {
    type: "linkPreview",
    hasAttachment: "false",
    idTemp: generateGUID(),
    linkDescription: linkData.Description || "",
    linkImage: linkData.ImageUrl || "",
    linkSource: urlObject.origin,
    linkTitle: linkData.Title || "",
    linkUrl: linkData.Url || url,
  };
};

/**
 * Escape special regex characters in a string
 * @param string - String to escape
 * @returns Escaped string safe for regex
 */
export const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

/**
 * Format message for server by converting @mentions to server format
 * @param text - Raw message text with @mentions
 * @param members - Array of members that can be mentioned
 * @returns Formatted message with mentions in server format
 */
export const formatMessageForServer = (text: string, members: Array<{ Id: string; ContactName: string }>): string => {
  let normalized = text.normalize("NFC");

  // Handle @All mention first
  const allMentionRegex = /\B@All(?=\W|$)/gu;
  normalized = normalized.replace(allMentionRegex, "@[All](userid:0)");

  // Sort members by name length (longest first) to avoid partial matches
  const sortedMembers = [...members].sort((a, b) => b.ContactName.length - a.ContactName.length);

  // Replace each member mention
  sortedMembers.forEach((member) => {
    const normalizedName = member.ContactName.normalize("NFC");
    const displayMention = `@${normalizedName}`;
    const mentionRegex = new RegExp(`\\B${escapeRegExp(displayMention)}(?=\\W|$)`, "gu");
    const serverFormat = `@[${normalizedName}](userid:${member.Id})`;
    normalized = normalized.replace(mentionRegex, serverFormat);
  });

  return normalized;
};

/**
 * Build album metadata from uploaded image URLs
 * @param imageUrls - Array of uploaded image URLs
 * @returns Album metadata object for API
 */
export const buildAlbumMetadata = (imageUrls: string[]): Record<string, any> => {
  return {
    type: "album",
    hasAttachment: "true",
    urls: imageUrls.join(";"),
    thumbs: imageUrls.join(";"), // Using same URLs for thumbnails
  };
};

/**
 * Build single image metadata
 * @param imageUrl - Uploaded image URL
 * @param file - Original file object
 * @returns Image metadata object for API
 */
export const buildImageMetadata = (imageUrl: string, file: File): Record<string, any> => {
  return {
    hasAttachment: "true",
    attachmentUrl: imageUrl,
    sizeLong: String(file.size),
    name: file.name,
    width: "0",
    height: "0",
    mimeType: file.type || "image/jpeg",
    sizeString: `${(file.size / 1024).toFixed(1)}KB`,
  };
};

/**
 * Clean upload URL by removing Office Apps prefix
 * @param url - URL to clean
 * @returns Cleaned URL
 */
export const cleanUploadUrl = (url: string): string => {
  return url.replace("https://view.officeapps.live.com/op/view.aspx?src=", "");
};

/**
 * Build file metadata
 * @param fileUrl - Uploaded file URL (will be cleaned)
 * @param file - Original file object
 * @returns File metadata object for API
 */
export const buildFileMetadata = (fileUrl: string, file: File): Record<string, any> => {
  const cleanedUrl = cleanUploadUrl(fileUrl);
  return {
    hasAttachment: "true",
    attachmentUrl: cleanedUrl,
    sizeLong: String(file.size),
    name: file.name,
    width: "0",
    height: "0",
    mimeType: file.type || "application/octet-stream",
    sizeString: `${(file.size / 1024).toFixed(1)}KB`,
  };
};

/**
 * Build video metadata
 * @param videoUrl - Uploaded video URL (will be cleaned)
 * @param file - Original file object
 * @param thumbnailUrl - Optional thumbnail URL (will be cleaned)
 * @returns Video metadata object for API
 */
export const buildVideoMetadata = (videoUrl: string, file: File, thumbnailUrl?: string): Record<string, any> => {
  const cleanedUrl = cleanUploadUrl(videoUrl);
  const cleanedThumbnail = thumbnailUrl ? cleanUploadUrl(thumbnailUrl) : "";

  return {
    hasAttachment: "true",
    attachmentUrl: cleanedUrl,
    sizeLong: String(file.size),
    name: file.name,
    width: "0",
    height: "0",
    mimeType: file.type || "application/octet-stream",
    sizeString: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
    thumbnailUrl: cleanedThumbnail,
  };
};
